/**
 * Pure, framework-free logic extracted out of `VideoDirectorCreatePage.tsx`
 * / `VideoParametersPanel.tsx` so it can be exercised by a plain Node
 * unit test (`frontend/scripts/videoDirectorWorkflow.test.mjs`) without
 * needing a React/DOM test runner — same convention as
 * `workspaceNavConfig.ts` / `workspaceNavConfig.test.mjs`.
 */
import type { VideoCreationMode, VirtualIpContextRef } from './videoDirectorTypes'

/** Minimal shape this pure function needs from `VideoGenConfig` — kept
 * local (rather than importing the full API type) so this module stays
 * dependency-free and trivially portable to a plain Node test file. */
export interface VideoGenConfigDurationCap {
  max_duration_seconds?: Record<string, number>
}

export function resolveMaxDurationForQuality(cfg: VideoGenConfigDurationCap | null, qualityTier: string): number {
  const cap = cfg?.max_duration_seconds
  if (!cap) return qualityTier === '1080P' ? 10 : 15
  return cap[qualityTier] ?? cap.default ?? 15
}

export function clampDuration(duration: number, max: number): number {
  return Math.min(Math.max(1, duration), max)
}

/** Spec §十四 — Next: Script/Storyboard requires a selected, non-stale proposal. */
export function canAdvanceFromStep1(
  selectedConceptId: string | null,
  selectionStale = false,
): boolean {
  return !!selectedConceptId && !selectionStale
}

/** Step 2 gate — requires confirmed, non-stale storyboard to unlock Step 3. */
export function canAdvanceFromStep2(
  confirmedBatchId: string | null,
  storyboardStale = false,
  directorSelectionStale = false,
): boolean {
  return !!confirmedBatchId && !storyboardStale && !directorSelectionStale
}

/** Step 3 unlocked only when Step 2 storyboard is confirmed and current. */
export function canAdvanceToStep3(
  confirmedBatchId: string | null,
  storyboardStale = false,
  directorSelectionStale = false,
): boolean {
  return canAdvanceFromStep2(confirmedBatchId, storyboardStale, directorSelectionStale)
}

/** Step 4 Gate (VIDEO_KEYFRAME_DESIGN task spec §44) — only unlocked once
 * Step 3's confirmed keyframe design has every required frame generated
 * and current (the backend's `step4_unlocked` is already the single
 * source of truth combining confirmation + validity + staleness). */
export function canAdvanceToStep4(step4Unlocked: boolean): boolean {
  return step4Unlocked
}

export function canGenerateStoryboard(
  selectedConceptId: string | null,
  directorSelectionStale: boolean,
  generating: boolean,
): boolean {
  return !!selectedConceptId && !directorSelectionStale && !generating
}

/** Spec 测试 §3 — switching to Independent mode must never leak a
 * previously-resolved Virtual IP context into the payload. */
export function resolveVirtualIpRefForMode(
  mode: VideoCreationMode,
  virtualIpRef: VirtualIpContextRef | null,
): VirtualIpContextRef | null {
  return mode === 'virtual_ip' ? virtualIpRef : null
}

export function computeGenerateDisabledReason(userIntent: string): 'empty_intent' | null {
  return userIntent.trim().length === 0 ? 'empty_intent' : null
}

export function canSubmitLegacyGeneration(
  userIntent: string,
  refImageStatus: 'uploading' | 'ready' | 'failed' | undefined,
  refVideoStatus: 'uploading' | 'ready' | 'failed' | undefined,
): boolean {
  return (
    userIntent.trim().length > 0 &&
    refImageStatus !== 'uploading' &&
    refVideoStatus !== 'uploading' &&
    refImageStatus !== 'failed' &&
    refVideoStatus !== 'failed'
  )
}
